// Add a rule, or change one (Phase 4b plan §2.4). One card, steps revealed one at a time: who, where,
// shoppers we can't locate (nothing pre-selected), then what the rule will do.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { dashboardAPI } from '../../../services/dashboard-api';
import { track } from '../../../utils/analytics';
import StoreForm from './StoreForm';
import { EMPTY_FORM, cardToForm, formAsCard, formToBody, isScoped, nextHint, ruleSentence, townHint } from './copy';

const ADD_STORE = '__add';

const Step = ({ n, title, hint, children }) => (
  <Box component="fieldset" sx={{ border: 0, p: 0, m: 0 }}>
    <Stack direction="row" spacing={1.5} alignItems="center" component="legend" sx={{ mb: 1 }}>
      <Box
        aria-hidden
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 600,
          fontSize: 14,
        }}
      >
        {n}
      </Box>
      <Typography variant="h6" component="h2" sx={{ fontSize: 18 }}>
        {title}
      </Typography>
    </Stack>
    {hint && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {hint}
      </Typography>
    )}
    {children}
  </Box>
);

const Choice = ({ value, label, hint }) => (
  <Box>
    <FormControlLabel value={value} control={<Radio />} label={label} sx={{ minHeight: 44 }} />
    {hint && (
      <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: -0.75, mb: 0.5 }}>
        {hint}
      </Typography>
    )}
  </Box>
);

export default function RuleBuilder() {
  const { ruleId } = useParams();
  const [searchParams] = useSearchParams();
  const retailerParam = searchParams.get('retailer') || undefined;
  const location = useLocation();
  const navigate = useNavigate();

  const [page, setPage] = useState(null); // GET /partner-rules: partners, own cards, can_edit
  const [stores, setStores] = useState([]);
  const [countries, setCountries] = useState(['NL']);
  const [regions, setRegions] = useState([]);
  const [towns, setTowns] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      dashboardAPI.getPartnerRules(retailerParam),
      dashboardAPI.getStores(retailerParam),
      dashboardAPI.getPartnerRuleRegions(retailerParam),
    ])
      .then(([p, s, r]) => {
        setPage(p);
        setStores(s.stores);
        setCountries(s.countries);
        setRegions(r.regions);
        const card = ruleId && p.own.find((c) => c.id === ruleId);
        if (card) setForm(cardToForm(card));
        else if (ruleId) setError('This rule was changed or turned off. Go back to your rules.');
      })
      .catch((e) => setError(e.message));
  }, [ruleId, retailerParam]);

  const store = stores.find((s) => s.id === form.storeId);
  useEffect(() => {
    if (!store || !(form.km >= 1 && form.km <= 100)) {
      setTowns(null);
      return undefined;
    }
    const t = setTimeout(() => {
      dashboardAPI.getTowns(store.lat, store.lon, form.km).then(setTowns).catch(() => setTowns(null));
    }, 300);
    return () => clearTimeout(t);
  }, [store, form.km]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const preview = useMemo(
    () => formAsCard(form, { partners: page?.partners, stores, regions }),
    [form, page, stores, regions]
  );
  const hint = nextHint(form);
  const townsLine = townHint(towns, form.km);
  const back = { pathname: '/settings/partner-rules', search: location.search };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (ruleId) {
        await dashboardAPI.changePartnerRule(ruleId, formToBody(form), retailerParam);
        track('Partner Rule Changed');
        navigate(back, { state: { notice: 'Saved. The change works at once, on your site and on partner sites.' } });
      } else {
        await dashboardAPI.createPartnerRule(formToBody(form), retailerParam);
        track('Partner Rule Created', { who: form.who, where: form.where });
        navigate(back, {
          state: {
            notice:
              'Your rule is on. It works at once, on your site and on partner sites. You can turn it off with its switch at any time.',
          },
        });
      }
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  if (!page && !error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        <Link component={RouterLink} to={back}>
          Partner rules
        </Link>{' '}
        › {ruleId ? 'Change a rule' : 'Add a rule'}
      </Typography>

      {page && !page.can_edit ? (
        <Alert severity="info">Only admin users can change partner rules. Ask an admin in your team.</Alert>
      ) : (
        <Card>
          <CardContent sx={{ px: { xs: 2, sm: 3 }, '&:last-child': { pb: 2.5 } }}>
            <Stack spacing={3.5}>
              <Step n={1} title="Who do you want to exclude?">
                <RadioGroup
                  value={form.who}
                  onChange={(e) =>
                    set({ who: e.target.value, where: e.target.value === 'all' && form.where === 'everywhere' ? '' : form.where })
                  }
                >
                  <Choice
                    value="all"
                    label="Exclude all partners"
                    hint="Don't show sizes from the network in the situations you choose next."
                  />
                  <Choice
                    value="one"
                    label="Exclude a specific partner"
                    hint="Don't show this partner's sizes in the situations you choose next. Sizes from other partners still show."
                  />
                </RadioGroup>
                {form.who === 'one' && (
                  <TextField
                    select
                    label="Partner"
                    value={form.partner}
                    onChange={(e) => set({ partner: e.target.value })}
                    sx={{ mt: 1, width: '100%', maxWidth: 360 }}
                  >
                    {(page?.partners || []).map((p) => (
                      <MenuItem key={p.retailer_id} value={p.retailer_id}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Step>

              {form.who && (
                <Step n={2} title="Where?">
                  <RadioGroup value={form.where} onChange={(e) => set({ where: e.target.value })}>
                    <Choice value="store" label="Near a store" hint="Shoppers within a distance of one of your stores." />
                    <Choice value="region" label="In a province" hint="Shoppers anywhere in one province." />
                    {form.who === 'one' && (
                      <Choice value="everywhere" label="Everywhere" hint="Every shopper, wherever they are." />
                    )}
                  </RadioGroup>

                  {form.where === 'region' && (
                    <TextField
                      select
                      label="Province"
                      value={form.region}
                      onChange={(e) => set({ region: e.target.value })}
                      sx={{ mt: 1, width: '100%', maxWidth: 360 }}
                    >
                      {regions.map((r) => (
                        <MenuItem key={r.code} value={r.code}>
                          {r.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}

                  {form.where === 'store' && (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                      {adding ? (
                        <StoreForm
                          retailerId={retailerParam}
                          countries={countries}
                          onCancel={() => setAdding(false)}
                          onSaved={async (saved) => {
                            const s = await dashboardAPI.getStores(retailerParam);
                            setStores(s.stores);
                            setAdding(false);
                            set({ storeId: saved.id });
                          }}
                        />
                      ) : stores.length ? (
                        <TextField
                          select
                          label="Store"
                          value={form.storeId}
                          onChange={(e) => (e.target.value === ADD_STORE ? setAdding(true) : set({ storeId: e.target.value }))}
                          sx={{ width: '100%', maxWidth: 480 }}
                        >
                          {stores.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                              {s.name}
                              {s.address_line ? ` — ${s.address_line}, ${s.city || ''}` : ''}
                            </MenuItem>
                          ))}
                          <MenuItem value={ADD_STORE}>Add another store…</MenuItem>
                        </TextField>
                      ) : (
                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" gap={1}>
                          <Typography color="text.secondary">You have no stores saved yet.</Typography>
                          <Button variant="outlined" onClick={() => setAdding(true)} sx={{ minHeight: 44 }}>
                            Add a store
                          </Button>
                        </Stack>
                      )}
                      {store && !adding && (
                        <>
                          <TextField
                            label="Distance in km"
                            type="number"
                            value={form.km}
                            onChange={(e) => set({ km: e.target.value === '' ? '' : Number(e.target.value) })}
                            inputProps={{ min: 1, max: 100, inputMode: 'numeric' }}
                            sx={{ maxWidth: 200 }}
                          />
                          {townsLine && (
                            <Typography variant="body2" color="text.secondary">
                              {townsLine}
                            </Typography>
                          )}
                        </>
                      )}
                    </Stack>
                  )}
                </Step>
              )}

              {form.who && isScoped(form) && (
                <Step n={3} title="Shoppers we can't locate" hint="Phones and VPNs often hide where a shopper is. Choose one.">
                  <RadioGroup
                    value={form.unknownExcluded === null ? '' : form.unknownExcluded ? 'exclude' : 'show'}
                    onChange={(e) => set({ unknownExcluded: e.target.value === 'exclude' })}
                  >
                    <Choice value="show" label="Don't exclude them" hint="The rule only works for shoppers we can locate." />
                    <Choice value="exclude" label="Exclude them too" hint="We treat them as if they are in the area." />
                  </RadioGroup>
                </Step>
              )}

              {preview && (
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, mb: 0.5 }}>
                    What this rule will do
                  </Typography>
                  <Typography>{ruleSentence(preview, true)}</Typography>
                </Box>
              )}

              {error && <Alert severity="error">{error}</Alert>}

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                gap={1.5}
              >
                <Typography variant="body2" color="text.secondary">
                  {hint || (ruleId ? 'The change starts at once.' : 'The rule starts at once. You can turn it off at any time.')}
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button component={RouterLink} to={back} sx={{ minHeight: 44 }}>
                    Cancel
                  </Button>
                  <Button variant="contained" onClick={submit} disabled={!!hint || busy || adding} sx={{ minHeight: 44 }}>
                    {busy ? <CircularProgress size={18} color="inherit" /> : ruleId ? 'Save' : 'Turn on rule'}
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}
